// eslint-disable-next-line local-rules-imports/prefer-root-alias-imports -- Vite configuration imports these schemas before application aliases are available.
import { parseGenerationMetrics } from '../../01-models/generation-metrics';
import { z } from 'zod';

export const chatProfileSchema = z.enum(['webgpu-wasm64-jspi', 'webgpu-wasm32-jspi', 'webgpu-wasm32-asyncify', 'cpu-wasm32']);
// Diagnostic artifacts are deliberately not part of ordinary chat selection.
export const gpuProfilingProfile = 'webgpu-wasm64-jspi-gpu-profile' as const;
export const profileSchema = z.enum([...chatProfileSchema.options, gpuProfilingProfile]);
export type LlamaCppProfile = z.infer<typeof profileSchema>;

export function usesWebGpu({ profile }: { profile: LlamaCppProfile }): boolean {
  switch (profile) {
  case 'webgpu-wasm64-jspi-gpu-profile':
  case 'webgpu-wasm64-jspi':
  case 'webgpu-wasm32-jspi':
  case 'webgpu-wasm32-asyncify': return true;
  case 'cpu-wasm32': return false;
  default: {
    const exhaustive: never = profile;
    throw new Error(`Unhandled profile: ${exhaustive}`);
  }
  }
}

export const minimumChatContextTokens = 512;
export const maximumChatContextTokens = 1048576;
export const chatContextTokensSchema = z.number().int().min(minimumChatContextTokens).max(maximumChatContextTokens);
export const defaultChatContextTokens = 32768;
export const visionBf16StorageSchema = z.enum(['legacy-expand', 'retain-checked']);

export const runtimeOptionsSchema = z.object({
  visionBf16Storage: visionBf16StorageSchema.optional(),
  contextTokens: chatContextTokensSchema.optional(),
  profile: z.union([z.literal('auto'), profileSchema]),
}).strict();
export type RuntimeOptions = z.infer<typeof runtimeOptionsSchema>;
/** The public selection value crosses data boundaries unchanged. Operations
 * resolve its meaning against the current model files. */
export const modelIdSchema = z.string();
export const modelSchema = z.object({
  modelId: modelIdSchema,
  source: z.object({ kind: z.literal('host'), directoryId: z.string(), directoryName: z.string(), repository: z.string(), primaryRelativePath: z.string() }).strict().optional(),
  size: z.number().int().positive().max(Number.MAX_SAFE_INTEGER),
  importedAt: z.number().int().nonnegative(),
}).strict();
export const modelDirectoryInputSchema = z.object({ rootName: z.string().min(1), files: z.array(z.object({ relativePath: z.string().min(1), file: z.instanceof(File) }).strict()).min(1) }).strict();
export type ModelDirectoryInput = z.infer<typeof modelDirectoryInputSchema>;
export type LocalModel = z.infer<typeof modelSchema>;
export const modelsSchema = z.array(modelSchema);
export const errorCodeSchema = z.enum([
  'unavailable', 'invalid-gguf', 'duplicate-model', 'missing-model', 'storage-error',
  'runtime-error', 'template-unsupported', 'reasoning-unsupported', 'context-full', 'unsupported-input',
  'busy', 'aborted', 'worker-failed', 'audio-model-unsupported', 'audio-reference-required', 'audio-reference-invalid', 'audio-output-empty',
]);
export type ErrorCode = z.infer<typeof errorCodeSchema>;
export class LlamaCppBrowserError extends Error {
  constructor({ code }: { code: ErrorCode }) {
    super(`llama.cpp browser: ${code}`);
    this.name = 'LlamaCppBrowserError';
  }
}

export function errorCode({ error }: { error: unknown }): ErrorCode {
  if (error instanceof DOMException && error.name === 'AbortError') {
    return 'aborted';
  }
  if (error instanceof Error) {
    const parsed = errorCodeSchema.safeParse(error.message.startsWith('llama.cpp browser: ') ? error.message.slice('llama.cpp browser: '.length) : undefined);
    if (parsed.success) {
      return parsed.data;
    }
    if (error.name === 'AbortError') {
      return 'aborted';
    }
  }
  return 'runtime-error';
}

export const progressSchema = z.object({
  phase: z.enum(['importing', 'initializing', 'loading', 'prefill', 'generating', 'decoding-audio']),
  completed: z.number().nonnegative().finite(),
  total: z.number().nonnegative().finite(),
}).strict();
export type Progress = z.infer<typeof progressSchema>;
export type EngineState =
  | { status: 'unavailable' }
  | { status: 'idle' }
  | { status: 'working', progress: Progress }
  | { status: 'error', code: ErrorCode };
export const toolCallSchema = z.object({ id: z.string(), type: z.literal('function'), function: z.object({ name: z.string(), arguments: z.string() }).strict() }).strict();
const chatMessageSchema = z.object({
  role: z.enum(['system', 'user', 'assistant', 'tool']),
  content: z.union([z.string(), z.array(z.discriminatedUnion('type', [z.object({ type: z.literal('text'), text: z.string() }).strict(), z.object({ type: z.literal('image'), blob: z.instanceof(Blob) }).strict()]))]),
  reasoning_content: z.string().optional(),
  tool_calls: z.array(toolCallSchema).optional(),
  tool_call_id: z.string().optional(),
  name: z.string().optional(),
}).strict();
export const generationResultSchema = z.object({
  content: z.string(),
  reasoningContent: z.string(),
  toolCalls: z.array(toolCallSchema),
  finishReason: z.enum(['stop', 'length', 'stop_sequence']),
  generationMetrics: z.unknown().transform(value => parseGenerationMetrics({ value })).optional(),
}).strict();
// Draft arguments are native parser snapshots, which may normalize or revise
// incomplete JSON. Suffix patches keep these previews separate from final calls.
export const generationEventSchema = z.discriminatedUnion('type', [
  // Telemetry alone is tolerant; content/call envelopes below remain strict.
  z.object({ type: z.literal('generation_metrics'), metrics: z.unknown().transform(value => parseGenerationMetrics({ value })).optional() }),
  z.object({ type: z.literal('text'), text: z.string() }).strict(),
  z.object({ type: z.literal('reasoning'), text: z.string() }).strict(),
  z.object({ type: z.literal('tool_call_start'), index: z.number().int().nonnegative() }).strict(),
  z.object({
    type: z.literal('tool_call_draft'),
    index: z.number().int().nonnegative(),
    name: z.string().optional(),
    arguments: z.object({ offset: z.number().int().nonnegative(), text: z.string() }).strict().optional(),
  }).strict(),
  z.object({ type: z.literal('tool_call'), index: z.number().int().nonnegative(), toolCall: toolCallSchema }).strict(),
]);
export type GenerationEvent = z.infer<typeof generationEventSchema>;
export type GenerationCallback = ({ event }: { event: GenerationEvent }) => void | Promise<void>;
export type GenerationResult = z.infer<typeof generationResultSchema>;
export type GenerateInput = z.infer<typeof generateInputSchema>;
export const generateInputSchema = z.object({
  debug: z.enum(['off', 'on']).optional(),
  modelId: modelIdSchema,
  messages: z.array(chatMessageSchema).min(1),
  tools: z.array(z.object({ type: z.literal('function'), function: z.object({ name: z.string().min(1), description: z.string(), parameters: z.record(z.string(), z.json()) }).strict() }).strict()).optional(),
  reasoningEffort: z.enum(['none', 'low', 'medium', 'high']).optional(),
  temperature: z.number().min(0).max(10),
  topP: z.number().min(0).max(1),
  maxTokens: z.number().int().min(1).max(Number.MAX_SAFE_INTEGER).optional(),
  presencePenalty: z.number().min(-2).max(2),
  frequencyPenalty: z.number().min(-2).max(2),
  stop: z.array(z.string().min(1).max(512)).max(32),
  options: runtimeOptionsSchema,
}).strict();
export const TEST_ONLY = {
};
